import React from 'react';
import { pdf } from '@react-pdf/renderer';
import JSZip from 'jszip';
import CourseReimbursementPDF from '../../../components/PDFExport/CourseReimbursementPDF';
import LessonReimbursementPDF from '../../../components/PDFExport/LessonReimbursementPDF';
import SpeakerReimbursementPDF from '../../../components/PDFExport/SpeakerReimbursementPDF';
import {
  CourseReimbursementModule,
  EventReimbursementModule,
  LessonReimbursementModule,
  Reimbursement,
} from '../../../interfaces/reimbursment';

export type ReimbursementPDFType = 'course' | 'lesson' | 'speaker';

const sanitizeFileStem = (value: string | number | undefined | null) => {
  const sanitized = String(value || '')
    .trim()
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

  return sanitized || 'rimborso';
};

const ensurePdfFileName = (fileName: string) =>
  fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

const ensureZipFileName = (fileName: string) =>
  fileName.endsWith('.zip') ? fileName : `${fileName}.zip`;

const getUniqueFileName = (fileName: string, usedFileNames: Set<string>) => {
  const normalizedFileName = ensurePdfFileName(fileName);
  if (!usedFileNames.has(normalizedFileName)) {
    usedFileNames.add(normalizedFileName);
    return normalizedFileName;
  }

  const extensionIndex = normalizedFileName.lastIndexOf('.pdf');
  const fileStem = normalizedFileName.slice(0, extensionIndex);
  let index = 2;
  let uniqueFileName = `${fileStem}-${index}.pdf`;

  while (usedFileNames.has(uniqueFileName)) {
    index += 1;
    uniqueFileName = `${fileStem}-${index}.pdf`;
  }

  usedFileNames.add(uniqueFileName);
  return uniqueFileName;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getCourseReimbursementRole = (reimbursement: Reimbursement) =>
  reimbursement.attributes.treasurerCourse?.data
    ? 'Tesoriere'
    : 'Responsabile';

export const getCourseReimbursementPDFFileName = (
  reimbursement: Reimbursement
) => {
  const courseModule = reimbursement.attributes
    .module as CourseReimbursementModule;
  const role = getCourseReimbursementRole(reimbursement).toLowerCase();
  const courseName = courseModule.payment?.courseName;
  const fileStem = courseName
    ? `rimborso-corso-${sanitizeFileStem(courseName)}-${role}`
    : `rimborso-corso-${sanitizeFileStem(reimbursement.id)}-${role}`;

  return ensurePdfFileName(fileStem);
};

export const getLessonReimbursementPDFFileName = (
  reimbursement: Reimbursement
) => {
  const reimbursementModule = reimbursement.attributes
    .module as LessonReimbursementModule;
  const lessonName =
    reimbursement.attributes.lesson?.data?.attributes?.title ||
    reimbursementModule.payment?.lessonName;

  return ensurePdfFileName(
    `rimborso-lezione-${sanitizeFileStem(lessonName || reimbursement.id)}`
  );
};

export const getSpeakerReimbursementPDFFileName = (
  reimbursement: Reimbursement
) => {
  const reimbursementModule = reimbursement.attributes
    .module as EventReimbursementModule;
  const eventName =
    reimbursement.attributes.event?.data?.attributes?.title ||
    reimbursementModule.payment?.eventName;

  return ensurePdfFileName(
    eventName
      ? `rimborso-relatore-${sanitizeFileStem(eventName)}-${sanitizeFileStem(
          reimbursement.id
        )}`
      : `rimborso-relatore-${sanitizeFileStem(reimbursement.id)}`
  );
};

export const getReimbursementPDFFileName = (
  reimbursement: Reimbursement,
  type: ReimbursementPDFType
) => {
  if (type === 'course') return getCourseReimbursementPDFFileName(reimbursement);
  if (type === 'lesson') return getLessonReimbursementPDFFileName(reimbursement);
  return getSpeakerReimbursementPDFFileName(reimbursement);
};

const getReimbursementPDFDocument = (
  reimbursement: Reimbursement,
  type: ReimbursementPDFType
) => {
  if (type === 'course') {
    return <CourseReimbursementPDF reimbursement={reimbursement} />;
  }

  if (type === 'lesson') {
    return <LessonReimbursementPDF reimbursement={reimbursement} />;
  }

  return <SpeakerReimbursementPDF reimbursement={reimbursement} />;
};

interface DownloadReimbursementsPDFZipParams {
  reimbursements: Reimbursement[];
  type: ReimbursementPDFType;
  zipFileName: string;
}

export const downloadReimbursementsPDFZip = async ({
  reimbursements,
  type,
  zipFileName,
}: DownloadReimbursementsPDFZipParams) => {
  const zip = new JSZip();
  const usedFileNames = new Set<string>();

  for (const reimbursement of reimbursements) {
    const document = getReimbursementPDFDocument(reimbursement, type);
    const fileName = getUniqueFileName(
      getReimbursementPDFFileName(reimbursement, type),
      usedFileNames
    );
    const blob = await pdf(document).toBlob();

    zip.file(fileName, blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(zipBlob, ensureZipFileName(zipFileName));
};
